import React, { useState, useEffect, useCallback } from "react";
import {
  Save,
  Trash2,
  Plus,
  Edit2,
  X,
  Check,
  Upload,
  User,
  Briefcase,
  Award,
  Users,
  FileText,
  Video,
  GraduationCap,
  Phone,
  Key,
  LogOut,
  Eye,
  Settings,
  Image,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("adminToken"));
  const [password, setPassword] = useState("");
  const [activeSection, setActiveSection] = useState("hero");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [message, setMessage] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [uploading, setUploading] = useState(false);

  const sections = [
    { id: "hero", label: "Hero", icon: User, isSingle: true },
    { id: "about", label: "About", icon: User, isSingle: true },
    { id: "ventures", label: "Ventures", icon: Briefcase },
    { id: "achievements", label: "Achievements", icon: Award },
    { id: "leadership", label: "Leadership", icon: Users },
    { id: "media", label: "Media", icon: FileText },
    { id: "talks", label: "Talks", icon: Video },
    { id: "education", label: "Education", icon: GraduationCap },
    { id: "contact", label: "Contact", icon: Phone, isSingle: true },
    { id: "settings", label: "Settings", icon: Settings, isSettings: true },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/portfolio`);
      const portfolioData = await response.json();
      setData(portfolioData);
    } catch (error) {
      showMessage("Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchData();
      } else {
        handleLogout();
      }
    } catch {
      handleLogout();
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("adminToken", data.access_token);
        setToken(data.access_token);
        setPassword("");
      } else {
        showMessage("Invalid password", "error");
      }
    } catch {
      showMessage("Login failed", "error");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken(null);
    setData(null);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      showMessage("New passwords do not match", "error");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current,
          new_password: passwordData.new,
        }),
      });
      if (response.ok) {
        showMessage("Password changed successfully!");
        setPasswordData({ current: "", new: "", confirm: "" });
        setShowPasswordChange(false);
      } else {
        showMessage(
          "Failed to change password. Check current password.",
          "error"
        );
      }
    } catch {
      showMessage("Error changing password", "error");
    }
  };

  const handleFileUpload = async (e, onSuccess) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        onSuccess(result.url);
        showMessage("Image uploaded successfully!");
      } else {
        showMessage("Upload failed", "error");
      }
    } catch {
      showMessage("Error uploading file", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (section, itemData, itemId = null) => {
    setSaving(true);
    try {
      let url = `${API_BASE}/api/${section}`;
      let method = "POST";

      if (section === "hero" || section === "about" || section === "contact") {
        method = "PUT";
      } else if (itemId) {
        url = `${API_BASE}/api/${section}/${itemId}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(itemData),
      });

      if (response.ok) {
        showMessage("Saved successfully!");
        fetchData();
        setEditingItem(null);
        setCreatingNew(false);
        setNewItem({});
      } else {
        const err = await response.json();
        showMessage(err.detail || "Failed to save", "error");
      }
    } catch (error) {
      showMessage("Error saving data", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section, itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const response = await fetch(`${API_BASE}/api/${section}/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        showMessage("Deleted successfully!");
        fetchData();
      } else {
        showMessage("Failed to delete", "error");
      }
    } catch {
      showMessage("Error deleting item", "error");
    }
  };

  // Login Screen
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">Admin Panel</CardTitle>
            <p className="text-gray-400 text-sm">Enter password to access</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Login
              </Button>
            </form>
            {message && (
              <p
                className={`mt-4 text-center text-sm ${
                  message.type === "error" ? "text-red-400" : "text-green-400"
                }`}
              >
                {message.text}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const ImageUploadField = ({ label, value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload"
          className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
        />
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileUpload(e, onChange)}
          />
          <div
            className={`px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white flex items-center gap-2 ${
              uploading ? "opacity-50" : ""
            }`}
          >
            <Upload size={16} />
            {uploading ? "..." : "Upload"}
          </div>
        </label>
      </div>
      {value && (
        <img
          src={value}
          alt="Preview"
          className="mt-2 w-24 h-24 object-cover rounded-lg border border-gray-700"
        />
      )}
    </div>
  );

  const renderHeroEditor = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Name
        </label>
        <input
          type="text"
          value={data?.hero?.name || ""}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, name: e.target.value } })
          }
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Title
        </label>
        <input
          type="text"
          value={data.hero.title}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, title: e.target.value } })
          }
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={data.hero.description}
          onChange={(e) =>
            setData({
              ...data,
              hero: { ...data.hero, description: e.target.value },
            })
          }
          rows={3}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <ImageUploadField
        label="Profile Image"
        value={data.hero.image}
        onChange={(url) =>
          setData({ ...data, hero: { ...data.hero, image: url } })
        }
      />
      <Button
        onClick={() => handleSave("hero", data.hero)}
        disabled={saving}
        className="bg-green-600 hover:bg-green-700"
      >
        <Save size={16} className="mr-2" />
        {saving ? "Saving..." : "Save Hero"}
      </Button>
    </div>
  );

  const renderAboutEditor = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Bio
        </label>
        <textarea
          value={data.about.bio}
          onChange={(e) =>
            setData({ ...data, about: { ...data.about, bio: e.target.value } })
          }
          rows={5}
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Stats
        </label>
        {data.about.stats?.map((stat, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={stat.label}
              onChange={(e) => {
                const newStats = [...data.about.stats];
                newStats[index].label = e.target.value;
                setData({ ...data, about: { ...data.about, stats: newStats } });
              }}
              placeholder="Label"
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
            <input
              type="text"
              value={stat.value}
              onChange={(e) => {
                const newStats = [...data.about.stats];
                newStats[index].value = e.target.value;
                setData({ ...data, about: { ...data.about, stats: newStats } });
              }}
              placeholder="Value"
              className="w-24 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
        ))}
      </div>
      <Button
        onClick={() => handleSave("about", data.about)}
        disabled={saving}
        className="bg-green-600 hover:bg-green-700"
      >
        <Save size={16} className="mr-2" />
        {saving ? "Saving..." : "Save About"}
      </Button>
    </div>
  );

  const renderContactEditor = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Phone
        </label>
        <input
          type="text"
          value={data?.contact?.phone || ""}
          onChange={(e) =>
            setData({
              ...data,
              contact: { ...data.contact, phone: e.target.value },
            })
          }
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Location
        </label>
        <input
          type="text"
          value={data.contact.location}
          onChange={(e) =>
            setData({
              ...data,
              contact: { ...data.contact, location: e.target.value },
            })
          }
          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Social Links
        </label>
        {data.contact?.social?.map((social, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={social.platform}
              onChange={(e) => {
                const newSocial = [...data.contact.social];
                newSocial[index].platform = e.target.value;
                setData({
                  ...data,
                  contact: { ...data.contact, social: newSocial },
                });
              }}
              placeholder="Platform"
              className="w-32 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
            <input
              type="text"
              value={social.url}
              onChange={(e) => {
                const newSocial = [...data.contact.social];
                newSocial[index].url = e.target.value;
                setData({
                  ...data,
                  contact: { ...data.contact, social: newSocial },
                });
              }}
              placeholder="URL"
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
            />
          </div>
        ))}
      </div>
      <Button
        onClick={() => handleSave("contact", data.contact)}
        disabled={saving}
        className="bg-green-600 hover:bg-green-700"
      >
        <Save size={16} className="mr-2" />
        {saving ? "Saving..." : "Save Contact"}
      </Button>
    </div>
  );

  const renderSettingsPage = () => (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Key size={20} />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.current}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, current: e.target.value })
                }
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.new}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, new: e.target.value })
                }
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirm}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm: e.target.value })
                }
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                required
              />
            </div>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              <Key size={16} className="mr-2" />
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  const getFieldsForSection = (section) => {
    const fieldConfigs = {
      ventures: [
        { key: "name", label: "Company Name", required: true },
        { key: "role", label: "Role", required: true },
        { key: "location", label: "Location" },
        { key: "duration", label: "Duration" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "website", label: "Website URL" },
        { key: "logo", label: "Logo", type: "image" },
      ],
      achievements: [
        { key: "title", label: "Title", required: true },
        { key: "description", label: "Description" },
        { key: "category", label: "Category" },
        { key: "year", label: "Year" },
        { key: "logo", label: "Logo", type: "image" },
      ],
      leadership: [
        { key: "title", label: "Title", required: true },
        { key: "organization", label: "Organization", required: true },
        { key: "duration", label: "Duration" },
        { key: "logo", label: "Logo", type: "image" },
      ],
      media: [
        { key: "title", label: "Title", required: true },
        { key: "publication", label: "Publication" },
        { key: "date", label: "Date" },
        { key: "url", label: "Article URL" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "thumbnail", label: "Thumbnail", type: "image" },
        { key: "featured", label: "Featured", type: "checkbox" },
      ],
      talks: [
        { key: "title", label: "Title", required: true },
        { key: "event", label: "Event" },
        { key: "date", label: "Date" },
        { key: "videoUrl", label: "Video URL" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "thumbnail", label: "Thumbnail", type: "image" },
        { key: "featured", label: "Featured", type: "checkbox" },
      ],
      education: [
        { key: "institution", label: "Institution", required: true },
        { key: "degree", label: "Degree" },
        { key: "type", label: "Type" },
        { key: "duration", label: "Duration" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "logo", label: "Logo", type: "image" },
      ],
    };
    return fieldConfigs[section] || [];
  };

  const renderItemForm = (item, setItem, fields) => (
    <div className="space-y-3">
      {fields.map((field) => (
        <div key={field.key}>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            {field.label}{" "}
            {field.required && <span className="text-red-400">*</span>}
          </label>
          {field.type === "textarea" ? (
            <textarea
              value={item[field.key] || ""}
              onChange={(e) =>
                setItem({ ...item, [field.key]: e.target.value })
              }
              rows={2}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm"
            />
          ) : field.type === "checkbox" ? (
            <input
              type="checkbox"
              checked={item[field.key] || false}
              onChange={(e) =>
                setItem({ ...item, [field.key]: e.target.checked })
              }
              className="w-4 h-4"
            />
          ) : field.type === "image" ? (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={item[field.key] || ""}
                onChange={(e) =>
                  setItem({ ...item, [field.key]: e.target.value })
                }
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm"
                placeholder="URL or upload"
              />
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    handleFileUpload(e, (url) =>
                      setItem({ ...item, [field.key]: url })
                    )
                  }
                />
                <div className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm">
                  <Upload size={14} />
                </div>
              </label>
              {item[field.key] && (
                <img
                  src={item[field.key]}
                  alt=""
                  className="w-10 h-10 object-cover rounded"
                />
              )}
            </div>
          ) : (
            <input
              type="text"
              value={item[field.key] || ""}
              onChange={(e) =>
                setItem({ ...item, [field.key]: e.target.value })
              }
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm"
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderListEditor = (section, items) => {
    const fields = getFieldsForSection(section);

    return (
      <div className="space-y-4">
        {/* Create New Button */}
        {!creatingNew && (
          <Button
            onClick={() => {
              setCreatingNew(true);
              setNewItem({});
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Create New
          </Button>
        )}

        {/* Create New Form */}
        {creatingNew && (
          <Card className="bg-gray-800 border-gray-700 border-2 border-blue-500">
            <CardContent className="p-4">
              <h4 className="font-medium text-white mb-4 flex items-center gap-2">
                <Plus size={16} /> New Item
              </h4>
              {renderItemForm(newItem, setNewItem, fields)}
              <div className="flex gap-2 pt-4">
                <Button
                  size="sm"
                  onClick={() => handleSave(section, newItem)}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check size={14} className="mr-1" />{" "}
                  {saving ? "Creating..." : "Create"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCreatingNew(false);
                    setNewItem({});
                  }}
                  className="border-gray-600 text-gray-300"
                >
                  <X size={14} className="mr-1" /> Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Items */}
        {items?.map((item, index) => (
          <Card key={item.id} className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              {editingItem === item.id ? (
                <div>
                  {renderItemForm(
                    items[index],
                    (updated) => {
                      const newItems = [...items];
                      newItems[index] = updated;
                      setData({ ...data, [section]: newItems });
                    },
                    fields
                  )}
                  <div className="flex gap-2 pt-4">
                    <Button
                      size="sm"
                      onClick={() => handleSave(section, item, item.id)}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check size={14} className="mr-1" />{" "}
                      {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingItem(null)}
                      className="border-gray-600 text-gray-300"
                    >
                      <X size={14} className="mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {(item.logo || item.thumbnail) && (
                      <img
                        src={item.logo || item.thumbnail}
                        alt=""
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div>
                      <h4 className="font-medium text-white">
                        {item.title || item.name || item.institution}
                      </h4>
                      <p className="text-sm text-gray-400 line-clamp-1">
                        {item.description ||
                          item.role ||
                          item.degree ||
                          item.organization}
                      </p>
                      {item.featured && (
                        <Badge className="mt-1 bg-blue-600 text-xs">
                          Featured
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingItem(item.id)}
                      className="text-gray-400 hover:text-white h-8 w-8 p-0"
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(section, item.id)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const getSectionContent = () => {
    if (activeSection === "settings") return renderSettingsPage();
    if (activeSection === "hero") return renderHeroEditor();
    if (activeSection === "about") return renderAboutEditor();
    if (activeSection === "contact") return renderContactEditor();
    return renderListEditor(activeSection, data[activeSection] || []);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 border-r border-gray-800 p-4 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-white">Admin Panel</h1>
          <p className="text-sm text-gray-500">Portfolio CMS</p>
        </div>

        <nav className="space-y-1 flex-1">
          {sections?.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => {
                  setActiveSection(section.id);
                  setEditingItem(null);
                  setCreatingNew(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <Icon size={18} />
                {section.label}
              </button>
            );
          })}
        </nav>

        <div className="pt-4 border-t border-gray-800 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <Eye size={18} />
            View Site
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-red-400 transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {message && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg ${
              message.type === "error"
                ? "bg-red-900/50 text-red-300"
                : "bg-green-900/50 text-green-300"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white capitalize">
            {activeSection}
          </h2>
          <p className="text-gray-400">
            {activeSection === "settings"
              ? "Manage your account settings"
              : `Manage your ${activeSection} section content`}
          </p>
        </div>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-6">{getSectionContent()}</CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPage;
